
  import { Component, Vue } from "vue-property-decorator";
  import { inject } from 'inversify-props';
  import { eventBus, mcEvents, mcDate, mcString, mcCounts } from "@/utils";
  import { types } from "@/store";
  import { MealCountDto, SaveMealCountRequest } from "@/models";
  import { MealCountDtoViewModel } from "@/view-models";
  import moment from 'moment';
  import { routePaths } from '@/router';
  import { IMealCountService } from '@/services';

  @Component
  export default class ListCounts extends Vue {
    synced = "Not Synced";
    perPage = 10;
    currentPage = 1;
    fields = [
      { key: 'site', sortable: true },
      { key: 'meal', sortable: true },
      { key: 'program', sortable: true },
      { key: 'servingDate', sortable: true },
      { key: 'lastSynced', sortable: true }
    ];
    filterSyncOptions = ['All', 'Synced', 'Not Synced'];

    get mealCounts() {
      const unsynced = <SaveMealCountRequest[]>this.$store.getters[types.state.syncUpCounts];
      const counts = <MealCountDto[]>this.$store.getters[types.state.mealCountDtos];

      let result = <MealCountDtoViewModel[]>unsynced.map(c => this.mapSaveRequestToVm(c));

      // match dtos to requests to remove doubles from final list
      for (const dto of counts) {
        const index = unsynced.findIndex(c => mcCounts.matchMealCountKey(c.version, dto));
        if (index === -1) {
          result = [...result, new MealCountDtoViewModel(dto)];
        }
      }

      return result;
    }

    mapSaveRequestToVm(c: SaveMealCountRequest){
      const servingLines = c.servingLine ? [ c.servingLine ] : [];
      let dto = new MealCountDto(
        c.version.trackingId,
        c.version.siteId,
        c.version.programId,
        c.version.mealTypeId,
        c.version.mealServiceDate,
        c.version.versionDateTimeUtc,
        c.version.isNonCongregate,
        c.version.providedDays,
        c.receivedMeals,
        servingLines);
      return new MealCountDtoViewModel(dto);
    }

    sync() {
      eventBus.$emit(mcEvents.itemSaved);
    }

    formatDate(date, format) {
      if (!date) return '';
      return moment(date, moment.defaultFormatUtc).format(format);
    }

    sortData(leftMealCountsViewModel, rightMealCountsViewModel, key: string, asc: boolean) {
      if (key === 'lastSynced') {
        const leftLastSyncedDate = moment.utc(<Date>leftMealCountsViewModel.mealCounts.syncDateUtc);
        const rightLastSyncedDate = moment.utc(<Date>rightMealCountsViewModel.mealCounts.syncDateUtc);
        const result = mcDate.isAfter(leftLastSyncedDate, rightLastSyncedDate);
        return result;
      }
      else if (key === 'servingDate') {
        const leftServingDate = moment.utc(leftMealCountsViewModel.mealCounts.serviceDate);
        const rightServingDate = moment.utc(rightMealCountsViewModel.mealCounts.serviceDate);
        const result = mcDate.isAfter(leftServingDate, rightServingDate);
        return result;
      }
      else if (key === 'site') {
        const result = mcString.isAfter(leftMealCountsViewModel.siteName, rightMealCountsViewModel.siteName);
        return result;
      }
      else if (key === 'meal') {
        const result = mcString.isAfter(leftMealCountsViewModel.mealCounts.mealTypeId, rightMealCountsViewModel.mealCounts.mealTypeId);
        return result;
      }
      else if (key === 'program') {
        const result = mcString.isAfter(leftMealCountsViewModel.mealCounts.programId, rightMealCountsViewModel.mealCounts.programId);
        return result;
      }
      return null;
    }

    get counts() {
      let mealCounts = this.mealCounts.filter(mcvm => mcvm.canShow);

      if (this.synced === 'Synced') {
        mealCounts = mealCounts.filter(mcvm => mcvm.isSynced);
      }

      if (this.synced == 'Not Synced') {
        mealCounts = mealCounts.filter(mcvm => mcvm.isUnsynced);
      }
      return mealCounts;
    }

    get rows() {
      return this.counts.length;
    }

    mounted() {
      this.collapseAllDetailRows();
    }

    toggleRow(row) {
      this.collapseAllDetailRowsExcept(row._mealCounts.trackingGuid);
      row._showDetails = !row._showDetails;
    }

    collapseAllDetailRows() {
      this.mealCounts.forEach(item => {
        this.$set(item, '_showDetails', false);
      });
    }

    collapseAllDetailRowsExcept(trackingGuid) {
      this.mealCounts.forEach(item => {
        if (item.mealCounts.trackingGuid != trackingGuid) {
          this.$set(item, '_showDetails', false);
        }
      });
    }

    goToCounts(vm: MealCountDtoViewModel) {
      const mealCounts = vm.mealCounts;
      const date = mcDate.toShortDate(mealCounts.mealServiceDate);
      const query = {
        sponsorId: vm.sponsorId.toString(),
        siteId: mealCounts.siteId.toString(),
        programId: mealCounts.programId,
        date: date,
        mealType: mealCounts.mealTypeId.toUpperCase(),
        type: 'receive'
      };
      this.$router.push({ name: routePaths.serveMeals.name, params: query });
    }
  }
